import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  FileText,
  Upload,
  Search,
  Filter,
  Download,
  Eye,
  Trash2,
  Loader2,
  X,
  Calendar,
  CheckCircle,
  AlertCircle,
  Clock,
  ChevronDown,
  FileImage,
  FileSpreadsheet,
  File,
  ExternalLink,
  Sparkles,
  List,
  RefreshCw,
} from "lucide-react";
import {
  uploadMyDoc,
  listMyDocs,
  getMyDoc,
  getMyDocSignedUrl,
  deleteMyDoc,
  fetchClaw9Accounts,
} from "../api/claw9";

// Source configuration matching Claw9 theme with Tailwind-safe class names
const SOURCES = {
  manual: { label: "Manual Upload", icon: Upload, bgClass: "bg-slate-500/15", textClass: "text-slate-400" },
  zalo: { label: "Zalo", icon: FileText, bgClass: "bg-blue-500/15", textClass: "text-blue-400" },
  telegram: { label: "Telegram", icon: FileText, bgClass: "bg-sky-500/15", textClass: "text-sky-400" },
  messenger: { label: "Messenger", icon: FileText, bgClass: "bg-indigo-500/15", textClass: "text-indigo-400" },
  instagram: { label: "Instagram", icon: FileText, bgClass: "bg-purple-500/15", textClass: "text-purple-400" },
};

// Status configuration with Tailwind-safe class names
const STATUS_CONFIG = {
  processing: { label: "Processing", icon: Clock, bgClass: "bg-amber-500/15", textClass: "text-amber-400" },
  ready: { label: "Ready", icon: CheckCircle, bgClass: "bg-emerald-500/15", textClass: "text-emerald-400" },
  failed: { label: "Failed", icon: AlertCircle, bgClass: "bg-red-500/15", textClass: "text-red-400" },
};

// File type icons
const getFileIcon = (fileType) => {
  if (!fileType) return File;
  if (fileType.includes("pdf")) return FileText;
  if (fileType.includes("image")) return FileImage;
  if (fileType.includes("sheet") || fileType.includes("excel") || fileType.includes("csv")) return FileSpreadsheet;
  if (fileType.includes("word") || fileType.includes("document")) return FileText;
  return File;
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

// Document Card Component
const DocumentCard = ({ doc, onView, onDownload, onDelete, isDeleting }) => {
  const FileIcon = getFileIcon(doc.file_type);
  const status = STATUS_CONFIG[doc.summary_status] || STATUS_CONFIG.processing;
  const StatusIcon = status.icon;
  const source = SOURCES[doc.source] || SOURCES.manual;

  return (
    <div className="group bg-white/5 hover:bg-white/8 border border-white/10 rounded-xl p-4 transition-all">
      <div className="flex items-start gap-3">
        {/* File Icon */}
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#ff4d4d]/10 flex items-center justify-center">
          <FileIcon className="w-5 h-5 text-[#ff4d4d]" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="text-sm font-medium text-white truncate" title={doc.filename}>
                {doc.filename}
              </h3>
              <div className="flex items-center gap-2 mt-1 text-xs text-white/50">
                <span>{formatFileSize(doc.file_size)}</span>
                <span>•</span>
                <span>{formatDate(doc.uploaded_at)}</span>
              </div>
            </div>

            {/* Status Badge */}
            <div className={`flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${status.bgClass} ${status.textClass}`}>
              <StatusIcon className="w-3 h-3" />
              {status.label}
            </div>
          </div>

          {/* Source Badge */}
          <div className="flex items-center gap-2 mt-2">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${source.bgClass} ${source.textClass}`}>
              {source.label}
            </span>
            {doc.channel && doc.channel !== doc.source && (
              <span className="text-xs text-white/40">via {doc.channel}</span>
            )}
          </div>

          {/* Summary Preview */}
          {doc.summary && (
            <p className="mt-2 text-xs text-white/60 line-clamp-2">
              {doc.summary}
            </p>
          )}

          {/* Key Points */}
          {doc.key_points && doc.key_points.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {doc.key_points.slice(0, 2).map((point, idx) => (
                <span key={idx} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-white/5 text-white/60">
                  <Sparkles className="w-3 h-3 text-[#ff4d4d]" />
                  {point.length > 40 ? point.substring(0, 40) + "..." : point}
                </span>
              ))}
              {doc.key_points.length > 2 && (
                <span className="text-xs text-white/40">+{doc.key_points.length - 2} more</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-white/5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => onView(doc)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 hover:text-white text-xs transition-colors"
        >
          <Eye className="w-3.5 h-3.5" />
          View
        </button>
        <button
          onClick={() => onDownload(doc)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 hover:text-white text-xs transition-colors"
        >
          <Download className="w-3.5 h-3.5" />
          Download
        </button>
        <button
          onClick={() => onDelete(doc)}
          disabled={isDeleting}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs transition-colors disabled:opacity-50"
        >
          {isDeleting ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Trash2 className="w-3.5 h-3.5" />
          )}
          Delete
        </button>
      </div>
    </div>
  );
};

// Document Preview Modal
const DocumentPreviewModal = ({ doc, onClose, onDownload }) => {
  const [signedUrl, setSignedUrl] = useState(null);
  const [isLoadingUrl, setIsLoadingUrl] = useState(false);
  const status = STATUS_CONFIG[doc.summary_status] || STATUS_CONFIG.processing;
  const StatusIcon = status.icon;
  const FileIcon = getFileIcon(doc.file_type);

  useEffect(() => {
    const loadSignedUrl = async () => {
      if (doc.file_type?.includes("pdf") || doc.file_type?.includes("image")) {
        setIsLoadingUrl(true);
        const result = await getMyDocSignedUrl(doc.id);
        if (result.success && result.data?.url) {
          setSignedUrl(result.data.url);
        }
        setIsLoadingUrl(false);
      }
    };
    loadSignedUrl();
  }, [doc.id, doc.file_type]);

  const canPreview = doc.file_type?.includes("pdf") || doc.file_type?.includes("image");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-[#0a0f1a] border border-white/10 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#ff4d4d]/10 flex items-center justify-center">
              <FileIcon className="w-5 h-5 text-[#ff4d4d]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">{doc.filename}</h2>
              <div className="flex items-center gap-2 text-xs text-white/50">
                <span>{formatFileSize(doc.file_size)}</span>
                <span>•</span>
                <span>{formatDate(doc.uploaded_at)}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onDownload(doc)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white transition-colors"
            >
              <Download className="w-4 h-4" />
              Download
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/5 text-white/50 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Left: Preview or Info */}
            <div className="space-y-4">
              {/* Status & Source */}
              <div className="flex items-center gap-2">
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${status.bgClass} ${status.textClass}`}>
                  <StatusIcon className="w-4 h-4" />
                  {status.label}
                </div>
                <span className="px-3 py-1.5 rounded-full text-sm bg-white/5 text-white/60">
                  {SOURCES[doc.source]?.label || doc.source}
                </span>
              </div>

              {/* Preview Area */}
              {canPreview && signedUrl ? (
                <div className="aspect-[4/3] bg-white/5 rounded-xl overflow-hidden">
                  {doc.file_type?.includes("image") ? (
                    <img src={signedUrl} alt={doc.filename} className="w-full h-full object-contain" />
                  ) : (
                    <iframe
                      src={signedUrl}
                      title={doc.filename}
                      className="w-full h-full"
                    />
                  )}
                </div>
              ) : isLoadingUrl ? (
                <div className="aspect-[4/3] bg-white/5 rounded-xl flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-[#ff4d4d] animate-spin" />
                </div>
              ) : (
                <div className="aspect-[4/3] bg-white/5 rounded-xl flex flex-col items-center justify-center text-white/40">
                  <FileIcon className="w-16 h-16 mb-2" />
                  <p className="text-sm">Preview not available</p>
                  <button
                    onClick={() => onDownload(doc)}
                    className="mt-3 flex items-center gap-2 px-4 py-2 rounded-lg bg-[#ff4d4d]/10 text-[#ff4d4d] hover:bg-[#ff4d4d]/20 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Download to view
                  </button>
                </div>
              )}

              {/* Metadata */}
              <div className="bg-white/5 rounded-xl p-4 space-y-3">
                <h3 className="text-sm font-medium text-white">Document Info</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-white/40">File Type</span>
                    <p className="text-white/80">{doc.file_type || "Unknown"}</p>
                  </div>
                  <div>
                    <span className="text-white/40">Extraction</span>
                    <p className="text-white/80">{doc.extraction_method || "Pending"}</p>
                  </div>
                  {doc.channel && (
                    <div>
                      <span className="text-white/40">Channel</span>
                      <p className="text-white/80">{doc.channel}</p>
                    </div>
                  )}
                  {doc.thread_id && (
                    <div>
                      <span className="text-white/40">Thread ID</span>
                      <p className="text-white/80 truncate" title={doc.thread_id}>{doc.thread_id}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Summary & Key Points */}
            <div className="space-y-4">
              {/* Summary */}
              <div className="bg-white/5 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-[#ff4d4d]" />
                  <h3 className="text-sm font-medium text-white">AI Summary</h3>
                </div>
                {doc.summary_status === "processing" ? (
                  <div className="flex items-center gap-2 text-white/50">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Generating summary...</span>
                  </div>
                ) : doc.summary_status === "failed" ? (
                  <p className="text-sm text-red-400">Failed to generate summary</p>
                ) : doc.summary ? (
                  <p className="text-sm text-white/70 leading-relaxed">{doc.summary}</p>
                ) : (
                  <p className="text-sm text-white/40">No summary available</p>
                )}
              </div>

              {/* Key Points */}
              <div className="bg-white/5 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <List className="w-4 h-4 text-[#ff4d4d]" />
                  <h3 className="text-sm font-medium text-white">Key Points</h3>
                </div>
                {doc.key_points && doc.key_points.length > 0 ? (
                  <ul className="space-y-2">
                    {doc.key_points.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-white/70">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#ff4d4d]/10 text-[#ff4d4d] flex items-center justify-center text-xs">
                          {idx + 1}
                        </span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                ) : doc.summary_status === "processing" ? (
                  <div className="flex items-center gap-2 text-white/50">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Extracting key points...</span>
                  </div>
                ) : (
                  <p className="text-sm text-white/40">No key points extracted</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Upload Zone Component
const UploadZone = ({ onUpload, isUploading }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      onUpload(files);
    }
  }, [onUpload]);

  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      onUpload(files);
    }
    // Reset input so same file can be selected again
    e.target.value = "";
  }, [onUpload]);

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => !isUploading && fileInputRef.current?.click()}
      className={`
        relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
        ${isDragging
          ? "border-[#ff4d4d] bg-[#ff4d4d]/10"
          : "border-white/20 hover:border-white/40 hover:bg-white/5"
        }
        ${isUploading ? "opacity-50 cursor-not-allowed" : ""}
      `}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.xls,.xlsx,.csv"
      />

      {isUploading ? (
        <div className="flex flex-col items-center">
          <Loader2 className="w-10 h-10 text-[#ff4d4d] animate-spin mb-3" />
          <p className="text-white/70">Uploading...</p>
        </div>
      ) : (
        <>
          <Upload className={`w-10 h-10 mx-auto mb-3 ${isDragging ? "text-[#ff4d4d]" : "text-white/40"}`} />
          <p className="text-white/70 mb-1">
            {isDragging ? "Drop files here" : "Drag & drop files here"}
          </p>
          <p className="text-white/40 text-sm">
            or click to browse • PDF, Word, Excel, Images
          </p>
        </>
      )}
    </div>
  );
};

// Main MyDocs Component
const MyDocs = ({ userEmail }) => {
  // Account state - fetched dynamically
  const [accountId, setAccountId] = useState(null);
  const [isLoadingAccount, setIsLoadingAccount] = useState(true);

  const [documents, setDocuments] = useState([]);
  const [groupedDocs, setGroupedDocs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [deletingDocId, setDeletingDocId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    source: "",
    status: "",
    date_from: "",
    date_to: "",
  });

  // Fetch account on mount
  useEffect(() => {
    const loadAccount = async () => {
      try {
        const result = await fetchClaw9Accounts();
        const accounts = result.accounts || [];
        if (accounts.length > 0) {
          setAccountId(accounts[0].account_id);
        } else {
          console.warn('[MyDocs] No accounts found');
        }
      } catch (err) {
        console.error('[MyDocs] Failed to load account:', err);
      } finally {
        setIsLoadingAccount(false);
      }
    };
    loadAccount();
  }, []);

  // Load documents
  const loadDocuments = useCallback(async () => {
    if (!accountId) return;

    try {
      setIsLoading(true);
      setError(null);

      const params = { ...filters };
      if (searchTerm) params.search = searchTerm;

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const result = await listMyDocs(accountId, params);

      if (result.success && result.data) {
        setGroupedDocs(result.data.groups || []);
        // Flatten for total count
        const allDocs = (result.data.groups || []).flatMap(g => g.items || []);
        setDocuments(allDocs);
      } else {
        setError(result.error || "Failed to load documents");
      }
    } catch (err) {
      console.error("Load documents error:", err);
      setError(err.message || "Failed to load documents");
    } finally {
      setIsLoading(false);
    }
  }, [accountId, filters, searchTerm]);

  useEffect(() => {
    if (accountId) {
      loadDocuments();
    }
  }, [accountId, loadDocuments]);

  // Handle file upload
  const handleUpload = async (files) => {
    if (!accountId) {
      setError("Account not loaded. Please try again.");
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      for (const file of files) {
        const result = await uploadMyDoc(file, { source: "manual", accountId });
        if (!result.success) {
          setError(result.error || `Failed to upload ${file.name}`);
          break;
        }
      }
      // Refresh list
      await loadDocuments();
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.message || "Failed to upload files");
    } finally {
      setIsUploading(false);
    }
  };

  // Handle download
  const handleDownload = async (doc) => {
    try {
      const result = await getMyDocSignedUrl(doc.id);
      if (result.success && result.data?.url) {
        window.open(result.data.url, "_blank");
      } else {
        setError(result.error || "Failed to get download URL");
      }
    } catch (err) {
      console.error("Download error:", err);
      setError(err.message || "Failed to download file");
    }
  };

  // Handle delete
  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.filename}"? This cannot be undone.`)) {
      return;
    }

    setDeletingDocId(doc.id);
    try {
      const result = await deleteMyDoc(doc.id);
      if (result.success) {
        await loadDocuments();
      } else {
        setError(result.error || "Failed to delete document");
      }
    } catch (err) {
      console.error("Delete error:", err);
      setError(err.message || "Failed to delete document");
    } finally {
      setDeletingDocId(null);
    }
  };

  // Stats
  const totalDocs = documents.length;
  const readyDocs = documents.filter(d => d.summary_status === "ready").length;
  const processingDocs = documents.filter(d => d.summary_status === "processing").length;

  return (
    <div className="h-full flex flex-col p-6 overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-semibold text-white">My Documents</h1>
            <p className="text-sm text-white/50">
              Upload and manage your documents with AI-powered summaries
            </p>
          </div>
          <button
            onClick={loadDocuments}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {/* Stats Cards */}
        <div className="flex gap-3 mb-4">
          <div className="flex-1 bg-white/5 rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 text-white/50 text-sm mb-1">
              <FileText className="w-4 h-4" />
              Total Documents
            </div>
            <p className="text-2xl font-semibold text-white">{totalDocs}</p>
          </div>
          <div className="flex-1 bg-white/5 rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 text-emerald-400/70 text-sm mb-1">
              <CheckCircle className="w-4 h-4" />
              Ready
            </div>
            <p className="text-2xl font-semibold text-emerald-400">{readyDocs}</p>
          </div>
          <div className="flex-1 bg-white/5 rounded-xl p-4 border border-white/5">
            <div className="flex items-center gap-2 text-amber-400/70 text-sm mb-1">
              <Clock className="w-4 h-4" />
              Processing
            </div>
            <p className="text-2xl font-semibold text-amber-400">{processingDocs}</p>
          </div>
        </div>

        {/* Upload Zone */}
        <UploadZone onUpload={handleUpload} isUploading={isUploading} />

        {/* Search and Filters */}
        <div className="flex items-center gap-3 mt-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#ff4d4d]/50"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-colors ${
              showFilters
                ? "bg-[#ff4d4d]/10 border-[#ff4d4d]/30 text-[#ff4d4d]"
                : "bg-white/5 border-white/10 text-white/70 hover:text-white"
            }`}
          >
            <Filter className="w-4 h-4" />
            Filters
            <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? "rotate-180" : ""}`} />
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mt-3 p-4 bg-white/5 border border-white/10 rounded-xl grid grid-cols-4 gap-4">
            <div>
              <label className="block text-xs text-white/50 mb-1">Source</label>
              <select
                value={filters.source}
                onChange={(e) => setFilters({ ...filters, source: e.target.value })}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[#ff4d4d]/50"
              >
                <option value="">All Sources</option>
                {Object.entries(SOURCES).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-white/50 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[#ff4d4d]/50"
              >
                <option value="">All Status</option>
                {Object.entries(STATUS_CONFIG).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-white/50 mb-1">From Date</label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[#ff4d4d]/50"
              />
            </div>
            <div>
              <label className="block text-xs text-white/50 mb-1">To Date</label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[#ff4d4d]/50"
              />
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex-shrink-0 mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3 text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Document List */}
      <div className="flex-1 overflow-auto">
        {(isLoading || isLoadingAccount) ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 text-[#ff4d4d] animate-spin" />
          </div>
        ) : groupedDocs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-white/40">
            <FileText className="w-16 h-16 mb-4 opacity-50" />
            <p className="text-lg">No documents found</p>
            <p className="text-sm">Upload your first document to get started</p>
          </div>
        ) : (
          <div className="space-y-6">
            {groupedDocs.map((group) => (
              <div key={group.date_key}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex items-center gap-2 text-white/70">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm font-medium">{group.date_label}</span>
                  </div>
                  <div className="flex-1 h-px bg-white/10" />
                  <span className="text-xs text-white/40">
                    {group.total} document{group.total !== 1 ? "s" : ""}
                  </span>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {group.items.map((doc) => (
                    <DocumentCard
                      key={doc.id}
                      doc={doc}
                      onView={setSelectedDoc}
                      onDownload={handleDownload}
                      onDelete={handleDelete}
                      isDeleting={deletingDocId === doc.id}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {selectedDoc && (
        <DocumentPreviewModal
          doc={selectedDoc}
          onClose={() => setSelectedDoc(null)}
          onDownload={handleDownload}
        />
      )}
    </div>
  );
};

export default MyDocs;
